import React, { useState, useEffect, useCallback } from 'react';
import BannerCarousel from './BannerCarousel';
import CategoryBar from './CategoryBar';
import VideoCard from './VideoCard';
import { SiYoutubeshorts } from 'react-icons/si';
import { API_BASE_URL, normalizeMediaUrl } from './config';
import './VideoGrid.css';

// Helper to format duration seconds to mm:ss or hh:mm:ss
function formatDuration(sec) {
  if (!sec && sec !== 0) return '0:00';
  const total = parseInt(sec, 10);
  if (isNaN(total)) return String(sec);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const padSec = seconds < 10 ? `0${seconds}` : `${seconds}`;
  if (hours > 0) {
    const padMin = minutes < 10 ? `0${minutes}` : `${minutes}`;
    return `${hours}:${padMin}:${padSec}`;
  }
  return `${minutes}:${padSec}`;
}

// Helper to format relative time
function formatRelativeTime(dateStr) {
  if (!dateStr) return 'Recently';
  const date = new Date(dateStr);
  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diffSec < 60) return 'Just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs} hour${diffHrs > 1 ? 's' : ''} ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
  const diffYears = Math.floor(diffMonths / 12);
  return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
}

// Map a raw backend video object to VideoCard-compatible shape
function mapVideo(v) {
  return {
    id: String(v.id),
    videoUrlId: v.video_url_id,
    title: v.title || 'Untitled Video',
    channelName: v.channel_name || v.handle_name || 'Speedo Creator',
    handleName: v.handle_name || '',
    channelId: v.channel_id || null,
    channelAvatar: v.channel_avatar || '',
    verified: v.verified !== undefined ? Boolean(Number(v.verified)) : true,
    views: v.views ? `${v.views}` : '0',
    timestamp: formatRelativeTime(v.created_date),
    duration: formatDuration(v.duration),
    rawDuration: typeof v.duration === 'number' ? v.duration : parseInt(v.duration, 10) || 0,
    video_url: v.video_url ? normalizeMediaUrl(v.video_url) : undefined,
    video_144_url: v.video_144_url ? normalizeMediaUrl(v.video_144_url) : undefined,
    thumbnailUrl: normalizeMediaUrl(v.thumbnail_url) || (process.env.PUBLIC_URL || '') + '/img/banner.png',
  };
}

const FETCH_HEADERS = {
  token: 'c7e993808a344da9d59cfa20fa8b6061',
  devicetype: '0',
  request_type: 'USER',
};

export default function VideoGrid({ selectedCategory: initialCategory = 'All', onNavigate }) {
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [videos, setVideos] = useState([]);
  const [shorts, setShorts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchLiveVideos = useCallback(async (category = 'All') => {
    setIsLoading(true);
    try {
      // 'All' → latest videos; specific category → filtered endpoint
      const url = category === 'All'
        ? `${API_BASE_URL}/api/v1/new-videos?limit=24&offset=0`
        : `${API_BASE_URL}/api/v1/videos/categories/${encodeURIComponent(category)}?limit=24&offset=0`;

      const res = await fetch(url, { headers: FETCH_HEADERS });

      if (res.ok) {
        const json = await res.json();
        const rawVideos = json?.data?.videos || [];
        // User requested to hide all existing old videos completely
        const validVideos = rawVideos.filter(
          (v) => v && v.video_url_id && v.is_new_upload === true
        );
        setVideos(validVideos.map(mapVideo));
      }
    } catch (err) {
      console.warn('Failed to fetch live videos from backend:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchLiveShorts = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/shorts?limit=10&offset=0`, {
        headers: FETCH_HEADERS,
      });

      if (res.ok) {
        const json = await res.json();
        const rawShorts = json?.data?.videos || [];
        // Only keep 'Saanu Saariyan' (id 182) and 'GT Bike' (id 209)
        const ALLOWED_SHORT_IDS = ['182', '209', 182, 209];
        const filtered = rawShorts.filter((s) => {
          if (ALLOWED_SHORT_IDS.includes(s.id)) return true;
          const t = (s.title || '').toLowerCase();
          return t.includes('saanu') || t.includes('gt bike');
        });

        const mapped = filtered.map((s) => ({
          id: String(s.id),
          videoUrlId: s.video_url_id,
          title: s.title || 'Untitled Short',
          views: s.views ? `${s.views} views` : '0 views',
          thumbnailUrl: normalizeMediaUrl(s.thumbnail_url) || (process.env.PUBLIC_URL || '') + '/img/banner.png',
        }));
        setShorts(mapped);
      }
    } catch (err) {
      console.warn('Failed to fetch live shorts from backend:', err);
    }
  }, []);

  // Re-fetch videos whenever the selected category changes
  useEffect(() => {
    fetchLiveVideos(selectedCategory);
  }, [selectedCategory, fetchLiveVideos]);

  useEffect(() => {
    fetchLiveShorts();

    const handleRefresh = () => {
      fetchLiveShorts();
    };
    window.addEventListener('speedotube_video_published', handleRefresh);
    window.addEventListener('focus', handleRefresh);

    return () => {
      window.removeEventListener('speedotube_video_published', handleRefresh);
      window.removeEventListener('focus', handleRefresh);
    };
  }, [fetchLiveShorts]);

  const renderVideoCard = (video) => (
    <VideoCard
      key={video.id}
      video={video}
      onChannelClick={(handle) => {
        const cleanHandle = handle.startsWith('@') ? handle : `@${handle}`;
        const channelPath = `/channel/${encodeURIComponent(cleanHandle)}`;
        if (onNavigate) {
          onNavigate(channelPath);
        } else {
          window.history.pushState({}, '', channelPath);
          window.dispatchEvent(
            new CustomEvent('speedotube_navigate', {
              detail: { path: channelPath },
            })
          );
        }
      }}
      onVideoClick={(v) => {
        const isShort = Boolean(v.isShort || v.is_short === 1);
        const watchPath = isShort ? `/shorts/${v.videoUrlId || v.id}` : `/watch?v=${v.videoUrlId || v.id}`;
        if (onNavigate) {
          onNavigate(watchPath);
        } else {
          window.history.pushState({}, '', watchPath);
          window.dispatchEvent(
            new CustomEvent('speedotube_navigate', {
              detail: { path: watchPath, videoId: v.videoUrlId || v.id },
            })
          );
        }
      }}
    />
  );

  return (
    <div className="yt-grid-container">
      {/* Top Advertisements / Featured Banner Carousel */}
      <BannerCarousel onNavigate={onNavigate} />

      {/* Keywords / Category Chip Row — live from njsapitube */}
      <CategoryBar
        selectedCategory={selectedCategory}
        onSelectCategory={(cat) => setSelectedCategory(cat)}
      />

      {/* First Row of Videos (3 Videos Per Row) */}
      {videos.length > 0 && (
        <div className="yt-video-grid">
          {videos.slice(0, 3).map(renderVideoCard)}
        </div>
      )}

      {/* YouTube Shorts Shelf */}
      {shorts.length > 0 && (
        <div className="yt-shorts-shelf-section">
          <div className="yt-shorts-shelf-header">
            <div className="yt-shorts-shelf-title-group">
              <span className="yt-shorts-shelf-icon">
                <SiYoutubeshorts size={24} />
              </span>
              <h2 className="yt-shorts-shelf-title">Shorts</h2>
            </div>
          </div>

          <div className="yt-shorts-shelf-grid">
            {shorts.slice(0, 6).map((short) => (
              <div
                key={short.id}
                className="yt-shorts-shelf-card"
                onClick={() => {
                  const watchPath = `/shorts/${short.videoUrlId || short.id}`;
                  if (onNavigate) {
                    onNavigate(watchPath);
                  } else {
                    window.history.pushState({}, '', watchPath);
                    window.dispatchEvent(
                      new CustomEvent('speedotube_navigate', {
                        detail: { path: watchPath, videoId: short.videoUrlId || short.id },
                      })
                    );
                  }
                }}
              >
                <div className="yt-shorts-shelf-thumb-wrapper">
                  <img
                    src={short.thumbnailUrl}
                    alt={short.title}
                    className="yt-shorts-shelf-thumb-img"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = (process.env.PUBLIC_URL || '') + '/img/banner.png';
                    }}
                  />
                  <div className="yt-shorts-shelf-gradient-overlay" />
                  <div className="yt-shorts-shelf-overlay-info">
                    <h3 className="yt-shorts-shelf-card-title">{short.title}</h3>
                    <p className="yt-shorts-shelf-card-views">{short.views}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Remaining Rows of Videos (3 Videos Per Row) */}
      {videos.length > 3 && (
        <div className="yt-video-grid" style={{ marginTop: '24px' }}>
          {videos.slice(3).map(renderVideoCard)}
        </div>
      )}

      {!isLoading && videos.length === 0 && shorts.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#888' }}>
          <p style={{ fontSize: '16px', fontWeight: '500' }}>No videos found in this category.</p>
          <p style={{ fontSize: '13px', color: '#666' }}>
            Try selecting a different category, or check back later.
          </p>
        </div>
      )}
    </div>
  );
}
